// **  store names
import { STORES } from "@constants/redux"

// ** main app Logger
import { appLogger } from "@helpers/logger"
import { handleApiAuthError } from "@helpers/authSession"
import { adminsApiDel, adminsApiGet, adminsApiPost, adminsApiPut } from "@helpers/adminsApiClient"

// ** constants
import { globalConst } from "@constants/global"

// **  utility
import { isEmpty, drop, orderBy,omit } from "lodash"

// ** fuzzy search support
import Fuse from "fuse.js"
import { devLog } from "@helpers/devLog"

const shouldSkipProtectedDashboardCalls = () => {
  // Server-authoritative admin dashboard: disable local fallback mode.
  return false
}

const LOCAL_AUCTIONS_STORAGE_KEY = "admin_local_auctions_v1"

const readLocalAuctions = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(LOCAL_AUCTIONS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (_error) {
    return []
  }
}

const writeLocalAuctions = (items) => {
  if (typeof window === "undefined") return
  window.localStorage.setItem(
    LOCAL_AUCTIONS_STORAGE_KEY,
    JSON.stringify(Array.isArray(items) ? items : [])
  )
}

const normalizeLocalAuctionStatus = (item) => {
  if (!item || typeof item !== "object") return item
  const now = Date.now()
  const startAtMs = item.startAt ? new Date(item.startAt).getTime() : null
  const expireAtMs = item.expireAt ? new Date(item.expireAt).getTime() : null
  const currentStatus = item.status || "PENDING"

  if (currentStatus === "ACTIVE" && expireAtMs && now >= expireAtMs) {
    return { ...item, status: "EXPIRED", updatedAt: new Date().toISOString() }
  }

  if (
    ["NOTSTARTED", "PENDING"].includes(currentStatus) &&
    startAtMs &&
    now >= startAtMs
  ) {
    return { ...item, status: "ACTIVE", updatedAt: new Date().toISOString() }
  }

  return item
}

const readAndHydrateLocalAuctions = () => {
  const items = readLocalAuctions()
  const hydrated = items.map(normalizeLocalAuctionStatus)
  const changed = JSON.stringify(items) !== JSON.stringify(hydrated)
  if (changed) {
    writeLocalAuctions(hydrated)
  }
  return hydrated
}

const upsertLocalAuction = (id, updater) => {
  const current = readAndHydrateLocalAuctions()
  const next = current.map((item) => (item.id === id ? updater(item) : item))
  writeLocalAuctions(next)
  return next
}

/** initla state for auth store  */
const initialState = {
  data: [],
  expiredData:[],
  current: {},
  perPageValues: [100, 150, 200],
  itemPerPage: 100,
  currentPage: 1,
  filters: {
    status: {
      value: "all",
      label: "label_all"
    },
    category: {
      value: "all",
      label: "All"
    }
  },
  message: "",
  keyword: null,
  action: globalConst.action.NONE
}

export const auctions = {
  state: initialState,

  reducers: {
    /**
     * Set translate message
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setMessage: (s, payload) => {
      return {
        ...s,
        message: payload
      }
    },
    setExpiredData: (s, payload) => {
      return {
        ...s,
        expiredData: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setData: (s, payload) => {
      return {
        ...s,
        data: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateItemCover: (s, payload) => {
      const index = s.data.findIndex((e) => e.id === payload.id)
      const item = s.data[index]
      item.cover = payload.cover
      const _data = s.data.filter((e) => e.id !== payload.id)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrentPage: (s, payload) => {
      return {
        ...s,
        currentPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateActivated: (s, payload) => {
      const index = s.data.findIndex((e) => e.id === payload.id)
      const item = s.data[index]
      item.status = payload.status
      const _data = s.data.filter((e) => e.id !== payload.id)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateItem: (s, payload) => {
      const index = s.data.findIndex((e) => e.id === payload.id)
      const item = s.data[index]
      item.images = item.images.filter((e) => e !== payload.image)
      const _data = s.data.filter((e) => e.id !== payload.id)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateItemImage: (s, payload) => {
      const index = s.data.findIndex((e) => e.id === payload.id)
      const item = s.data[index]
      item.images.push(payload.image)
      const _data = s.data.filter((e) => e.id !== payload.id)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateSingleAuction: (s, payload) => {
      const index = s.data.findIndex((e) => e.id === payload.id)
      const item = payload
      const _data = s.data.filter((e) => e.id !== payload.id)
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    pushNewData: (s, payload) => {
      return {
        ...s,
        data: [...s.data, payload]
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setFilters: (s, payload) => {
      return {
        ...s,
        filters: { ...s.filters, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setKeyword: (s, payload) => {
      return {
        ...s,
        keyword: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setitemPerPage: (s, payload) => {
      return {
        ...s,
        itemPerPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrent: (s, payload) => {
      return {
        ...s,
        current: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setAction: (s, payload) => {
      return {
        ...s,
        action: payload
      }
    }
  },
  selectors: (slice, createSelector) => ({
    /**
     *
     * @returns
     */
    error() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.stuffs],
        (stuffs, stuffsLoading) => {
          return stuffsLoading.error || ""
        }
      )
    },

    currentAuction(){
      return createSelector([slice],(auctions)=>auctions.current)
    },

    /**
     * success selector used in different
     * auth actions
     *
     * @returns {String}
     */
    success() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.auctions],
        (auctions, auctionsLoading) => auctionsLoading.success
      )
    },
    paginationData() {
      return createSelector([slice], (auctions) => {
        let _data = auctions.data
        if (!isEmpty(auctions.filters)) {
          const status = auctions.filters.status
          _data = !!status
            ? status.value !== "all"
              ? _data.filter((e) => e.status === status.value.toUpperCase())
              : _data
            : _data
        }

        if (auctions.filters.category !== null) {
          const category = auctions.filters.category

          _data =
            category.value !== "all"
              ? _data.filter((e) => e.category === category.value)
              : _data

          const offset = (auctions.currentPage - 1) * auctions.itemPerPage
          _data = drop(_data, offset).slice(0, auctions.itemPerPage)
        }
        if (!!auctions.keyword) {
          devLog(auctions.keyword)
          const options = {
            keys: [ "title", "titleAr", "descr", "descrAr"]
          }
          const fuse = new Fuse(_data, options)
          let data = fuse.search(auctions.keyword)
          data = data.map((e) => e.item)
          _data=data
          // const offset = (auctions.currentPage - 1) * auctions.itemPerPage
          // _data = drop(data, offset).slice(0, auctions.itemPerPage)
        }

        const offset = (auctions.currentPage - 1) * auctions.itemPerPage
        _data = drop(_data, offset).slice(0, auctions.itemPerPage)
        return orderBy(_data, (a) => new Date(a.updatedAt), ["desc"])
      })
    }
,
    paginationExpiredData() {
      return createSelector([slice], (auctions) => {
        let _data = auctions.expiredData || []
        
        // Safety check - ensure data is an array
        if (!Array.isArray(_data)) {
          return []
        }
        
        if (!isEmpty(auctions.filters)) {
          const status = auctions.filters.status
          _data = !!status
            ? status.value !== "all"
              ? _data.filter((e) => e.status === status.value.toUpperCase())
              : _data
            : _data
        }

        if (auctions.filters.category !== null) {
          const category = auctions.filters.category

          _data =
            category.value !== "all"
              ? _data.filter((e) => e.category === category.value)
              : _data
        }
        
        if (!!auctions.keyword) {
          const options = {
            keys: ["category", "title", "titleAr", "descr", "descrAr"]
          }
          const fuse = new Fuse(_data, options)
          let data = fuse.search(auctions.keyword)
          data = data.map((e) => e.item)
          _data = data
        }

        // Apply pagination only once at the end
        const offset = (auctions.currentPage - 1) * auctions.itemPerPage
        _data = drop(_data, offset).slice(0, auctions.itemPerPage)
        return orderBy(_data, (a) => new Date(a.updatedAt), ["desc"])
      })
    }
  }),
  effects: (dispatch) => ({
    /**
     *
     */
    async fetchAuctions() {
      appLogger.info("fetchAuctions", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.FETCH_AUCTIONS)
      if (shouldSkipProtectedDashboardCalls()) {
        this.setData(readAndHydrateLocalAuctions())
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const items = await adminsApiGet(`/auctions/v1/items/`)
        // const query = `
        // query MyQuery {
        //   fetchAuctions(type: "GENERAL",filter: {and: {status: {ne: "PAYED"},status: {ne: "CANCELED"}, and: {status: {ne: "SOLD"}}}}) {
        //     items {
        //       id,
        //        ref,
        //        category,
        //        images,
        //        titleAr,
        //        descr,
        //        descrAr,
        //        customer,
        //        winner,
        //        status,
        //        title,
        //          bid{
        //          auction,
        //          biddings
        //          },
        //          sale{
        //           auction,
        //           ref,
        //           currentPrice,
        //           lastPriceTime,
        //           total,
        //           amount,
        //           buyers
        //          }
        //        totalBid,
        //        type,
        //        data,
        //        currentIncr,
        //        minimumIncr,
        //        cover,
        //        createdAt,
        //        updatedAt,
        //        expireAt,
        //        startAt

        //          }
        //   }
        // }
        // `
        // const { data } = await API.post("auctionGraphql", "", {
        //   body: {
        //     query
        //   }
        // })

        // const items = data.fetchAuctions.items

        this.setData(items)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        if (handleApiAuthError(error)) return
        this.setAction(globalConst.action.NONE)
        this.setData([])
      }
    },
    /**
     *
     * @param {*} param0
     */
    async updateCover({ id, createdAt, cover }) {
      appLogger.info("updateCover", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.UPDATE_AUCTION_COVER)

      try {
        // Persist cover into listings.attributes via legacy compat controller.
        await adminsApiPut(`/auctions/v1/items/${id}`, { cover, createdAt })

        this.updateItemCover({ id, cover })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     */
    async fetchExpired() {
      appLogger.info("fetchExpired", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.FETCH_AUCTIONS)
      if (shouldSkipProtectedDashboardCalls()) {
        const localExpired = readAndHydrateLocalAuctions().filter(
          (item) => item.status === "EXPIRED"
        )
        this.setExpiredData(localExpired)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        // const query = `
        // query MyQuery {
        //   fetchAuctions(type: "GENERAL",filter: {and: {status: {ne: "PAYED"},status: {ne: "CANCELED"}, and: {status: {ne: "ACTIVE"}}}}) {
        //     items {
        //       id,
        //        ref,
        //        category,
        //        images,
        //        titleAr,
        //        descr,
        //        descrAr,
        //        customer,
        //        winner,
        //        status,
        //        title,
        //          bid{
        //          auction,
        //          biddings
        //          },
        //          sale{
        //           auction,
        //           ref,
        //           currentPrice,
        //           lastPriceTime,
        //           total,
        //           amount,
        //           buyers
        //          }
        //        totalBid,
        //        type,
        //        data,
        //        currentIncr,
        //        minimumIncr,
        //        cover,
        //        createdAt,
        //        updatedAt,
        //        expireAt,

        //          }
        //   }
        // }
        // `
        // const { data } = await API.post("auctionGraphql", "", {
        //   body: {
        //     query
        //   }
        // })
        const items = await adminsApiGet(`/auctions/v1/items?type=expired`)
     

        this.setExpiredData(items)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setExpiredData([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },
    /**
     *
     * @param {*} param0
     */
    async setWinner({ id, customer }) {
      appLogger.info("setWinner", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.SET_WINNER)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          winnerPk: customer,
          winner: customer,
          status: "SOLD",
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("label_winner_updated")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        await adminsApiPost(`/auctions/v1/items/${id}/winner`, { winnerPk: customer })
        this.setMessage("label_winner_updated")
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw error
      }
    },
    /**
     *
     * @param {*} id
     */
    async getAuction(id) {
      appLogger.info("getAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.GET_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const localAuction =
          readAndHydrateLocalAuctions().find((item) => item.id === id) || {}
        this.setCurrent(localAuction)
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        const result = await adminsApiGet(`/auctions/v1/items/${id}`)
       
        this.setCurrent(result)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        throw new Error(error)
      }
    },
    // SET_WINNER

    /**
     *
     * @param {*} payload
     */
    async updateAuction({ payload, id }) {
      appLogger.info("updateAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.UPDATE_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          ...payload,
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("msg_auction_updated")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        await adminsApiPut(`/auctions/v1/items/${id}`, { ...payload })
     
        this.setMessage("msg_auction_updated")
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} param0
     */
    async deleteAuction({ id, createdAt }) {
      appLogger.info("deleteAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.DELETE_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const next = readAndHydrateLocalAuctions().filter((item) => item.id !== id)
        writeLocalAuctions(next)
        this.setData(next)
        this.setMessage("msg_auction_deleted")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        // Legacy compat endpoint (no Amplify): remove auction by id.
        await adminsApiPost(`/auctions/${id}/rm`, { createdAt })
        this.setMessage("msg_auction_deleted")
      } catch (error) {
        throw new Error(error)
      }
    },


    /**
     *
     * @param {*} param0
     */
    async updateExpire({ createdAt, status, expireAt, id }) {
      appLogger.info("updateExpire", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.UPDATE_AUCTION_EXPIRE)

      try {
        // Legacy reschedule: controller expects both startAt + expireAt.
        const current = await adminsApiGet(`/auctions/v1/items/${id}`)
        const startAt = current?.startAt || current?.start_at
        await adminsApiPost(`/auctions/v1/items/${id}/activate`, {
          createdAt,
          startAt,
          expireAt,
        })
        this.setMessage("msg_auction_resheduled")
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} id
     */
    async createAuction(payload) {
      appLogger.info("adminsApi", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.CREATE_AUCTION)

      try {
        if (shouldSkipProtectedDashboardCalls()) {
          const now = new Date().toISOString()
          const localAuction = {
            ...omit(payload, ["type"]),
            id: `local-auction-${Date.now()}`,
            type: payload.type,
            createdAt: now,
            updatedAt: now,
            status: payload.status || "NOTSTARTED"
          }
          writeLocalAuctions([...readAndHydrateLocalAuctions(), localAuction])
          this.pushNewData(localAuction)
          this.setMessage("msg_auction_created")
          this.setAction(globalConst.action.NONE)
          return localAuction
        }
        await adminsApiPost(
          `/auctions/v1/items/${payload.type}`,
          { ...omit(payload, ["type"]) }
        )
        this.setMessage("msg_auction_created")
        this.setAction(globalConst.action.NONE)
        // this.setCurrent(result)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw error
      }
    },
    /**
     *
     * @param {*} param0
     */
    async deleteImage({ id, createdAt, image, images }) {
      appLogger.info("updateCover", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.DELETE_AUCTION_PHOTO)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          images: Array.isArray(images)
            ? images
            : (item.images || []).filter((img) => img !== image),
          updatedAt: now
        }))
        this.setData(next)
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        await adminsApiPut(`/auctions/v1/items/${id}`, { images, createdAt })


        this.updateItem({ id, image })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} param0
     */
    async activateAuction({
      id,
      createdAt,
      expireAt,
      startAt,
    }) {
      appLogger.info("activateAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.ACTIVATE_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          status: "NOTSTARTED",
          startAt: startAt || item.startAt,
          expireAt: expireAt || item.expireAt,
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("msg_auction_activated")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {

  
        await adminsApiPost(`/auctions/v1/items/${id}/activate`, { createdAt, expireAt, startAt })
     
        // this.updateActivated({ id, status })
        this.setMessage("msg_auction_activated")
        // this.updateItem({ id, image })
      } catch (error) {
        throw new Error(error)
      }
    },

    async hideAuction({
      id,
      createdAt
    }) {
      appLogger.info("hideAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.HIDE_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          hidden: true,
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("msg_auction_hidden")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {

  
        await adminsApiPost(`/auctions/v1/items/${id}/hide`, { createdAt })
     
        // this.updateActivated({ id, status })
        this.setMessage("msg_auction_hidden")
        // this.updateItem({ id, image })
      } catch (error) {
        throw new Error(error)
      }
    },

    async unhideAuction({
      id,
      createdAt
    }) {
      appLogger.info("unhideAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.UNHIDE_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          hidden: false,
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("msg_auction_unhidden")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {

  
        await adminsApiPost(`/auctions/v1/items/${id}/unhide`, { createdAt })
     
        // this.updateActivated({ id, status })
        this.setMessage("msg_auction_unhidden")
        // this.updateItem({ id, image })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} param0
     */
    async cancelAuction({ id, createdAt, status, expireAt, category, type }) {
      appLogger.info("cancelAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.CANCEL_AUCTION)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          status: status || "CANCELED",
          expireAt: expireAt || item.expireAt,
          updatedAt: now
        }))
        this.setData(next)
        this.setMessage("msg_auction_canceled")
        this.setAction(globalConst.action.NONE)
        return
      }

      try {
        // Best-effort "cancel" without legacy AWS: close the auction (admin endpoint).
        await adminsApiPost(`/auctions/${id}/close`, { createdAt, status, expireAt, category, type })
        this.updateActivated({ id, status })
        this.setMessage("msg_auction_canceled")
        // this.updateItem({ id, image })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} param0
     */
    async addImage({ id, createdAt, image, images }) {
      appLogger.info("addImage", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.ADD_AUCTION_PHOTO)

      const _images = images
      _images.push(image)
      if (shouldSkipProtectedDashboardCalls()) {
        const now = new Date().toISOString()
        const next = upsertLocalAuction(id, (item) => ({
          ...item,
          images: _images,
          updatedAt: now
        }))
        this.setData(next)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

        await adminsApiPut(`/auctions/v1/items/${id}`, { images: _images, createdAt })


        // this.updateItemImage({ id, image })
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     *
     * @param {*} id
     */
    async refreshAuction(id) {
      appLogger.info("refreshAuction", `[REDUX][${STORES.AUCTIONS}]`)
      this.setAction(globalConst.action.GET_AUCTION)

      try {
        const result = await adminsApiGet(`/auctions/v1/items/${id}`)

        this.updateSingleAuction(result)
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     *
     */
    onAcionReset() {
      this.setAction(globalConst.action.NONE)
    },
    /**
     *
     * @param {*} param0
     */
    resetCurrentRecord(payload = {}) {
      this.setCurrent(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemPerPageChange(payload) {
      this.setitemPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onFiltersChange(payload) {
      this.setFilters(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onCurrentPageChange(payload) {
      this.setCurrentPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onKeywordChange(payload) {
      this.setKeyword(payload)
    }
  })
}
